import { Avatar, Box, Center, Input, Text, useToast } from '@chakra-ui/react'
import handleErrorToast from '../toast/handleErrorToast'

const blacklistRegex =
  /\.(bat|exe|cmd|sh|php\d*|pl|cgi|386|dll|com|torrent|js|app|jar|pif|vb|vbscript|wsf|asp|cer|csr|jsp|drv|sys|ade|adp|bas|chm|cpl|crt|csh|fxp|hlp|hta|inf|ins|isp|jse|htaccess|htpasswd|ksh|lnk|mdb|mde|mdt|mdw|msc|msi|msp|mst|ops|pcd|prg|reg|scr|sct|shb|shs|url|vbe|vbs|wsc|wsf|wsh)$/i

const FileUpload = ({
  isAvatar,
  src,
  setFile,
  accept,
  many = false,
  detail,
  label,
  maxFileSize = 2,
  isDisabled,
  mb,
}: {
  maxFileSize?: number
  isAvatar?: boolean
  detail?: string
  label?: string
  src?: string | null
  setFile: (f: File | FileList | null) => void
  accept?: string
  many?: boolean
  isDisabled?: boolean
  mb?: string
}) => {
  const toast = useToast()

  const stripImageMetadata = (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (event: any) => {
        const img = new Image()
        img.src = event.target.result
        img.onload = () => {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          canvas.width = img.width
          canvas.height = img.height
          ctx?.drawImage(img, 0, 0)

          let outputFormat = file.type
          let quality = 1.0

          if (file.type === 'image/jpeg' || file.type === 'image/jpg') {
            quality = 0.98
          } else if (file.type === 'image/webp') {
            quality = 0.98
          } else {
            outputFormat = 'image/png'
            quality = 1.0
          }

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const strippedFile = new File([blob], file.name, { type: outputFormat })
                resolve(strippedFile)
              } else {
                reject(new Error('Image stripping failed'))
              }
            },
            outputFormat,
            quality
          )
        }
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  return (
    <Box position="relative">
      <Box
        display={isAvatar ? 'flex' : 'block'}
        bgColor="neutral.10"
        opacity={isDisabled ? '0.5' : '1'}
        minH="75px"
        borderRadius="10px"
        mb={mb ?? '24px'}
        border="1px dashed #C1C5C9"
        onDragOver={(e) => {
          e.preventDefault()
        }}
      >
        {isAvatar && <Avatar m="16px" src={src ?? ''} />}
        <Center padding="16px" display="flex" flexDir="column">
          <Text textStyle="b-sm" fontWeight="400" color="neutral.900" mb="4px" mr={isAvatar ? 'auto' : ''}>
            Drop or{' '}
            <Text as="span" fontWeight="510" display="inline" textDecor="underline">
              click here
            </Text>{' '}
            to upload {label ?? 'an image'}
          </Text>
          <Text textStyle="b-xs" fontWeight="400" color="neutral.500">
            {detail ?? 'PNG, JPG or WEBP. Image resolution 800x400px (max. 10 MB)'}
          </Text>
        </Center>
      </Box>
      <Input
        opacity="0"
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        type="file"
        accept={accept ?? undefined}
        minW="100%"
        h="80px"
        position="absolute"
        top="0"
        left="0"
        onChange={async (e) => {
          if (isDisabled) return
          const fileSize = maxFileSize * 1024 * 1024 // Convert maxFileSize from MB to bytes
          //const acceptedTypes = (accept ?? '').split(',').map((type) => type.trim().toLowerCase())

          if (e.target.files) {
            const files = Array.from(e.target.files)
            const oversizedFiles = files.filter((file) => file.size > fileSize)

            /*
            const invalidFiles = files.filter((file) => {
              const fileType = file.type.split('/')[0]
              return (
                !acceptedTypes.includes(fileType) &&
                !acceptedTypes.includes(`${fileType}/*`) &&
                !((accept ?? '') === file.type)
              )
            })
            


            if (invalidFiles.length > 0 && accept && accept !== '*') {
              handleErrorToast(toast, 'Invalid files!', `Only ${accept} files are allowed`)
              return
            }

            */

            if (oversizedFiles.length > 0) {
              handleErrorToast(toast, 'Invalid files!', `File size exceeds ${maxFileSize}MB`)
              return
            }
            const dangerousFiles = files.filter((file) => {
              const isDangerous = blacklistRegex.test(file.name)
              return isDangerous
            })
            if (dangerousFiles.length > 0) {
              handleErrorToast(
                toast,
                'Disallowed file type!',
                `Files with extensions like .exe, .js, .sh, etc. are not allowed.`
              )
              return
            }
            const strippedFiles = await Promise.all(
              files.map(async (file) => {
                if (file.type.startsWith('image/')) {
                  return stripImageMetadata(file)
                }
                return file
              })
            )

            if (many) setFile(strippedFiles as unknown as FileList)
            else setFile(strippedFiles[0])
          } else return
        }}
        value={''}
        multiple={many}
      />
    </Box>
  )
}

export default FileUpload
